// M33 — Plausible analytics init. Self-hosted at telemetry.isnull.io
// (Caddy + Docker on the maintainer's VPS, deployed in M31). The
// instance is shared with future projects; QuarterStack reports as
// `data-domain="quarterstack.app"` so events show up under the
// QuarterStack site in the Plausible admin without colliding with
// other tenants on the same host.
//
// Why a runtime-injected <script> (not a static tag in index.html):
//   1. `import.meta.env.MODE` lets us silently no-op on dev / preview
//      / vite test builds so localhost pageviews never pollute prod
//      data. A static tag would fire on every dev boot.
//   2. Single source of truth for the host + data-domain. Future tweaks
//      (custom events, outbound-link tracking, file downloads) extend
//      naturally from here.
//   3. Idempotent — the early return prevents duplicate script tags if
//      HMR or React's StrictMode happens to call init() twice.
//
// CSP note: `netlify.toml` allow-lists `https://telemetry.isnull.io`
// in both `script-src` (for the script load) and `connect-src` (for
// the event POSTs Plausible makes to /api/event). Without both, the
// dashboard would stay "awaiting first pageview" forever with red
// errors in the console.

const PLAUSIBLE_HOST = 'https://telemetry.isnull.io';
const PLAUSIBLE_DOMAIN = 'quarterstack.app';
const SCRIPT_MARKER_ATTR = 'data-plausible-init';

export function initAnalytics(): void {
  // Dev / preview / test — skip. Vite's `MODE` is 'production' only on
  // `vite build` output (i.e. what Netlify ships). Dev server and
  // `vite preview` both report 'development'; vitest reports 'test'.
  if (import.meta.env.MODE !== 'production') {
    if (import.meta.env.DEV) {
      console.info('[analytics] non-production mode, skipping Plausible init');
    }
    return;
  }

  // No-op in non-browser contexts (SSR safety; we're an SPA so this
  // mostly matters if something runs the module under Node).
  if (typeof document === 'undefined') return;

  // Idempotency guard — already initialized this page.
  if (document.querySelector(`script[${SCRIPT_MARKER_ATTR}]`)) return;

  const script = document.createElement('script');
  script.defer = true;
  script.src = `${PLAUSIBLE_HOST}/js/script.js`;
  script.setAttribute('data-domain', PLAUSIBLE_DOMAIN);
  script.setAttribute(SCRIPT_MARKER_ATTR, '1');
  document.head.appendChild(script);
}

// M41 — Custom event tracking. Plausible's default script.js exposes a
// global `window.plausible(eventName, { props })`. Events only land when
// the script is actually loaded — i.e. production (see initAnalytics's
// MODE gate); on dev / preview / test trackEvent is a guarded no-op.

interface PlausibleWindow extends Window {
  plausible?: (
    event: string,
    options?: { props?: Record<string, string | number | boolean> },
  ) => void;
}

/**
 * Report a custom event to Plausible. Best-effort and guarded: if the
 * script hasn't loaded (or at all), it no-ops. Analytics must never
 * throw into the app.
 */
export function trackEvent(
  event: string,
  props?: Record<string, string | number | boolean>,
): void {
  if (typeof window === 'undefined') return;
  const fn = (window as PlausibleWindow).plausible;
  if (typeof fn !== 'function') return;
  try {
    fn(event, props ? { props } : undefined);
  } catch {
    // Swallow — analytics failures are non-fatal.
  }
}
